import {
  Button,
  IconButton,
  TextField as MuiTextField,
  Paper,
  TextFieldProps,
  Typography,
} from "@mui/material";
import { DangerousSettingWarning } from "./DangerousSettingWarning";
import { DriveType } from "protos/atk/enums";
import { Field, FieldArray } from "formik";
import {
  Autocomplete as FormikAutocomplete,
  TextField as FormikTextField,
} from "formik-mui";
import { t } from "i18next";
import {
  TractorCalibration,
  TractorCalibrationConfig,
} from "protos/atk/calibration";
import { TypedPathTo } from "portal/utils/typedPath";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import React, { useMemo, useState } from "react";

const AUTOCOMPLETE_CONFIGS: { [driveType in DriveType]?: string[] } = {
  [DriveType.DRIVE_TYPE_FRONT_AXLE_STEER]: [
    "rtc/tractor_ctl/fw_conf/steering_conf/wheeled_tuning_params/kp",
    "rtc/tractor_ctl/fw_conf/steering_conf/wheeled_tuning_params/ki",
    "rtc/tractor_ctl/fw_conf/steering_conf/wheeled_tuning_params/kd",
    "rtc/tractor_ctl/fw_conf/steering_conf/wheeled_tuning_params/tau",
    "rtc/tractor_ctl/fw_conf/steering_conf/wheeled_tuning_params/dt",
    "rtc/tractor_ctl/fw_conf/steering_conf/wheeled_tuning_params/integral_limit",
    "rtc/tractor_ctl/fw_conf/wheel_angle_cal/right_lock_deg",
    "rtc/tractor_ctl/fw_conf/wheel_angle_cal/sensor_deg_per_bit",
    "rtc/tractor_ctl/viz/steering_multiplier",
  ],
  [DriveType.DRIVE_TYPE_DIFFERENTIAL_STEER]: [
    "rtc/tractor_ctl/fw_conf/steering_conf/tracked_tuning_params/kp",
    "rtc/tractor_ctl/fw_conf/steering_conf/tracked_tuning_params/ki",
    "rtc/tractor_ctl/fw_conf/steering_conf/tracked_tuning_params/kd",
    "rtc/tractor_ctl/fw_conf/steering_conf/tracked_tuning_params/tau",
    "rtc/tractor_ctl/fw_conf/steering_conf/tracked_tuning_params/dt",
    "rtc/tractor_ctl/fw_conf/steering_conf/tracked_tuning_params/integral_limit",
    "rtc/tractor_ctl/fw_conf/steering_conf/tracked_tuning_params/feed_forward_gain",
    "rtc/tractor_ctl/fw_conf/steering_conf/tracked_tuning_params/feed_forward_max",
    "rtc/tractor_ctl/fw_conf/steering_conf/tracked_tuning_params/slip_gain",
    "rtc/tractor_ctl/fw_conf/steering_conf/tracked_tuning_params/speed_gain",
    "rtc/tractor_ctl/fw_conf/steering_conf/tracked_tuning_params/mech_conv_factor",
    "rtc/tractor_ctl/viz/steering_multiplier",
  ],
};

const ExtraConfigRow: React.FC<{
  driveType: DriveType | undefined;
  basePath: TypedPathTo<TractorCalibrationConfig>;
  configuredPaths: Set<string>;
  config: TractorCalibrationConfig;
  disabled: boolean;
  onDelete: () => void;
}> = ({ driveType, basePath, onDelete, configuredPaths, disabled }) => {
  const { t } = useTranslation();
  const autocompletePaths = useMemo(
    () => (driveType ? AUTOCOMPLETE_CONFIGS[driveType] ?? [] : []),
    [driveType]
  );
  // filter out paths that already have a config
  const unconfiguredPaths = useMemo(
    () => autocompletePaths.filter((path) => !configuredPaths.has(path)),
    [autocompletePaths, configuredPaths]
  );

  return (
    <>
      <Field
        name={basePath.path.$path}
        component={FormikAutocomplete}
        freeSolo
        options={unconfiguredPaths}
        renderInput={(params: TextFieldProps) => (
          <MuiTextField
            {...params}
            label={t(
              "views.autotractor.equipment.tractors.calibration.extraConfigs.path"
            )}
          />
        )}
        disabled={disabled}
      />
      <Field
        name={basePath.floatVal.$path}
        component={FormikTextField}
        disabled={disabled}
        label={t(
          "views.autotractor.equipment.tractors.calibration.extraConfigs.value"
        )}
      />
      <IconButton onClick={onDelete} disabled={disabled}>
        <DeleteIcon />
      </IconButton>
    </>
  );
};

interface ExtraConfigsCalibrationSubformProps<
  TBasePath extends TypedPathTo<TractorCalibration>
> {
  basePath: TBasePath;
  driveType: DriveType | undefined;
  readOnly: boolean;
  values: TractorCalibration;
}

export const ExtraConfigsCalibrationSubform = <
  TBasePath extends TypedPathTo<TractorCalibration>
>({
  basePath,
  driveType,
  readOnly,
  values,
}: ExtraConfigsCalibrationSubformProps<TBasePath>): JSX.Element => {
  const [editingEnabled, setEditingEnabled] = useState<boolean>(false);
  const disabled = readOnly || !editingEnabled;

  const configuredPaths = useMemo(
    () => new Set(values.extraConfigs.map((config) => config.path)),
    [values.extraConfigs]
  );

  return (
    <Paper className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_min-content] gap-4 p-4">
      <Typography className="col-span-full font-bold">
        {t(
          "views.autotractor.equipment.tractors.calibration.extraConfigs.title"
        )}
      </Typography>
      <DangerousSettingWarning
        showConfirmationButton={!readOnly && !editingEnabled}
        onConfirm={() => setEditingEnabled(true)}
      />
      <FieldArray
        name={basePath.extraConfigs.$path}
        render={(arrayHelpers) => (
          <>
            {values.extraConfigs.map((config, index) => (
              <ExtraConfigRow
                key={config.path}
                driveType={driveType}
                basePath={basePath.extraConfigs[index]!}
                config={config}
                configuredPaths={configuredPaths}
                onDelete={() => arrayHelpers.handleRemove(index)()}
                disabled={disabled}
              />
            ))}
            <Button
              variant="contained"
              className="col-span-full"
              startIcon={<AddIcon />}
              onClick={() =>
                arrayHelpers.push(TractorCalibrationConfig.fromPartial({}))
              }
              disabled={disabled}
            >
              {t("utils.actionPhrases.addConfig")}
            </Button>
          </>
        )}
      />
    </Paper>
  );
};
